import { Color, Spacing } from '@persona-id/design-tokens';
import { VFC } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import { Trigger, TriggerEvent } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import {
  OverridableWorkflowStepActionKey,
  WorkflowRunSimulateContext,
} from 'dashboard/models/workflow-run-simulated';
import {
  INPUT_STEP_TOKEN,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepAll,
} from 'dashboard/models/workflow-version';
import { WORKFLOW_SIMULATE_CONTROL_PANEL_WIDTH } from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/constants';
import OverrideErrorBanner from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/OverrideErrorBanner';
import OverrideEvaluateCode from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/StepOverride/OverrideEvaluateCode';
import OverrideListMatch from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/StepOverride/OverrideListMatch';
import OverrideRunReport from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/StepOverride/OverrideRunReport';
import OverrideRunVerification from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/StepOverride/OverrideRunVerification';
import { StepOverrideBaseProps } from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/StepOverride/StepOverrideBaseProps';
import TriggerOverrideEvent from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/TriggerOverrideEvent/TriggerOverrideEvent';
import { headingMixins } from 'design-system/mixins/text';
import { FieldSchema } from 'lib/fields';
import { intersperse } from 'lib/utils/array';

const WorkflowSimulateControlPanelContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  width: ${WORKFLOW_SIMULATE_CONTROL_PANEL_WIDTH}px;
  flex: none;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 1px solid ${Color.BorderHairline};
`;

const SubtitleWrapper = styled.div`
  width: ${WORKFLOW_SIMULATE_CONTROL_PANEL_WIDTH}px;
  flex: none;
  padding: ${Spacing[5]};
  overflow-y: auto;
  overflow-x: hidden;
`;

const Wrapper = styled.div<{ active: boolean; errored: boolean }>`
  width: ${WORKFLOW_SIMULATE_CONTROL_PANEL_WIDTH}px;
  flex: none;
  padding: ${Spacing[3]} ${Spacing[5]};
  overflow-y: auto;
  overflow-x: hidden;
  ${({ active, errored }) => {
    return active && errored
      ? `
        border-right: 2px inset ${Color.CoreRed900};
      `
      : active
        ? `
        border-right: 3px inset ${Color.CorePurple800};
      `
        : null;
  }}
`;

const SubTitle = styled.div`
  ${headingMixins.sm}
`;

const Divider = styled.hr<{ full?: boolean }>`
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
`;

interface WorkflowSimulateControlPanelProps {
  activeNode: WorkflowStepAll | null;
  closePanel: () => void;
  setActiveNodeToken: (arg: string) => void;
  setFocusedNodeToken: (arg: string) => void;
  setSimulateContext: (arg: WorkflowRunSimulateContext) => void;
  simulateContext: WorkflowRunSimulateContext;
  setTriggerFieldSchemas: (fieldSchemas: FieldSchema[]) => void;
  triggerFieldSchemas: FieldSchema[];
  trigger: Trigger;
  steps: WorkflowStepAction[];
  validationErrors: WorkflowError[];
  disabled: boolean;
  requiredFields: string[];
  referencedFields: string[];
  triggerFieldDefinitionPanelOpen: boolean;
  toggleTriggerFieldDefinitionPanel: () => void;
  closeTriggerFieldDefinitionPanel: () => void;
}

const OVERRIDABLE_STEP_ACTION_KEY_TO_COMPONENT: Record<
  OverridableWorkflowStepActionKey,
  VFC<StepOverrideBaseProps>
> = {
  [WorkflowStepActionKey.ListMatch]: OverrideListMatch,
  [WorkflowStepActionKey.RunReportGeneric]: OverrideRunReport,
  [WorkflowStepActionKey.RunVerificationGeneric]: OverrideRunVerification,
  [WorkflowStepActionKey.EvaluateCode]: OverrideEvaluateCode,
};
const WorkflowSimulateControlPanel: VFC<WorkflowSimulateControlPanelProps> = ({
  activeNode,
  closePanel,
  setActiveNodeToken,
  setFocusedNodeToken,
  setSimulateContext,
  simulateContext,
  setTriggerFieldSchemas,
  triggerFieldSchemas,
  steps,
  trigger,
  validationErrors,
  disabled,
  requiredFields,
  referencedFields,
  triggerFieldDefinitionPanelOpen,
  toggleTriggerFieldDefinitionPanel,
  closeTriggerFieldDefinitionPanel,
}) => {
  const handleWrapperClick = (token: string) => {
    setFocusedNodeToken(token);
    setActiveNodeToken(token);
  };

  const nodes = [
    <Accordion
      justifyCaret='start'
      key={trigger.id}
      title='Trigger'
      titleGapSize={Spacing[0]}
      overrideExpanded={activeNode?.token === INPUT_STEP_TOKEN}
    >
      <OverrideErrorBanner
        validationError={validationErrors.find((error) => error.token === trigger.id)}
      />
      <TriggerOverrideEvent
        trigger={trigger as TriggerEvent}
        handleClose={closePanel}
        simulateContext={simulateContext}
        setSimulateContext={setSimulateContext}
        setTriggerFieldSchemas={setTriggerFieldSchemas}
        triggerFieldSchemas={triggerFieldSchemas}
        disabled={disabled}
        requiredFields={requiredFields}
        referencedFields={referencedFields}
        triggerFieldDefinitionPanelOpen={triggerFieldDefinitionPanelOpen}
        toggleTriggerFieldDefinitionPanel={toggleTriggerFieldDefinitionPanel}
        closeTriggerFieldDefinitionPanel={closeTriggerFieldDefinitionPanel}
      />
    </Accordion>,
    ...steps
      .map((step: WorkflowStepAction) => {
        const actionKey = step.action?.key as OverridableWorkflowStepActionKey;
        const OverrideComponent = OVERRIDABLE_STEP_ACTION_KEY_TO_COMPONENT[actionKey];
        if (OverrideComponent == null) {
          return null;
        }
        return (
          <Accordion
            justifyCaret='start'
            key={step.token}
            title={step.name}
            titleGapSize={Spacing[0]}
            overrideExpanded={step.token === activeNode?.token}
          >
            <OverrideErrorBanner
              validationError={validationErrors.find((error) => error.token === step.token)}
            />
            <OverrideComponent
              disabled={disabled}
              step={step}
              setSimulateContext={setSimulateContext}
              simulateContext={simulateContext}
            />
          </Accordion>
        );
      })
      .filter(Boolean),
  ]
    .map((elem, i) => {
      if (elem?.key != null && typeof elem.key == 'string') {
        return (
          <Wrapper
            active={activeNode?.token === elem.key}
            errored={validationErrors.some((error) => error.token === elem.key)}
            key={`workflow-simulate-control-panel__override-${i}`}
            id={`workflow-simulate-control-panel__override-${i}`}
            onClick={() => handleWrapperClick(elem.key as string)}
          >
            {elem}
          </Wrapper>
        );
      }
    })
    .filter(Boolean);

  return (
    <WorkflowSimulateControlPanelContainer
      aria-label='Step override panel'
      role='dialog'
      id='workflow-simulate-control-panel__container'
    >
      <SubtitleWrapper>
        <SubTitle>Simulate Overrides</SubTitle>
        Click a step, change the overrides, save and click run
      </SubtitleWrapper>
      <Divider />
      {intersperse(nodes, (idx) => (
        <Divider key={idx} />
      ))}
    </WorkflowSimulateControlPanelContainer>
  );
};

export default WorkflowSimulateControlPanel;
